"use client";

import React, { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import Link from "next/link";
import "./itsm.css";

/* Images — public/assets/images/ se serve hoti hain */
const IMG = "/assets/images";
const img = (name: string) => encodeURI(`${IMG}/${name}`);
const imgHero = img("itsm-hero.jpg");
const imgStory = img("Retail-&-CPG.jpg");
const imgCta = img("lets-banner.jpg");

/* All styling lives in itsm.css (no styling in this file). */

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>("[data-reveal]");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400) =>
  ({ "--it-delay": `${Math.min(i * step, max)}ms` } as React.CSSProperties);

/* ---------- Icons ---------- */

type IconKey =
  | "people" | "gear" | "db" | "shield" | "trend" | "clock" | "smile" | "check"
  | "bolt" | "ticket" | "factory" | "cart" | "bank" | "heart";

function Icon({ name }: { name: IconKey }) {
  const paths: Record<IconKey, ReactNode> = {
    people: (<><circle cx="9" cy="8" r="3" /><path d="M3 20c.4-3.6 2.6-5.4 6-5.4s5.6 1.8 6 5.4M16 5.5a3 3 0 0 1 0 5.8M18 14.8c1.8.6 2.8 2.3 3 5.2" /></>),
    gear: (<><circle cx="12" cy="12" r="3" /><path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M18.4 5.6l-1.8 1.8M7.4 16.6l-1.8 1.8" /></>),
    db: (<><ellipse cx="12" cy="5.5" rx="7" ry="2.8" /><path d="M5 5.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6M5 11.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6" /></>),
    shield: (<><path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.1 7.5 9.5 4.4-1.4 7.5-4.9 7.5-9.5V6L12 3Z" /><path d="m9 12 2.2 2.2L15.5 10" /></>),
    trend: (<><path d="M3 17l5-5 4 4 8-9" /><path d="M15 7h5v5" /></>),
    clock: (<><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>),
    smile: (<><circle cx="12" cy="12" r="8.5" /><path d="M8.5 14c.8 1.2 2 1.8 3.5 1.8s2.7-.6 3.5-1.8M9 9.5h.01M15 9.5h.01" /></>),
    check: (<><circle cx="12" cy="12" r="8.5" /><path d="m8.5 12.2 2.4 2.4 4.6-5" /></>),
    bolt: <path d="M13 3 5 13.5h6L10 21l8-10.5h-6L13 3Z" />,
    ticket: (<><path d="M3 8a2 2 0 0 0 0 4v0a2 2 0 0 1 0 4v2h18v-2a2 2 0 0 1 0-4v0a2 2 0 0 0 0-4V6H3v2Z" /><path d="M10 6v12" strokeDasharray="2 2" /></>),
    factory: <path d="M3 20V9l6 4V9l6 4V6h3v14H3ZM7 20v-3M11 20v-3M15 20v-3" />,
    cart: (<><circle cx="9" cy="19" r="1.6" /><circle cx="17" cy="19" r="1.6" /><path d="M3 4h2.5l2 11h10l2-8H7" /></>),
    bank: <path d="M3 9.5 12 4l9 5.5M5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18" />,
    heart: <path d="M12 20s-7.5-4.6-7.5-10A4.3 4.3 0 0 1 12 7.5 4.3 4.3 0 0 1 19.5 10c0 5.4-7.5 10-7.5 10Z" />,
  };

  return (
    <svg
      className="it-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

/* Partner logo: agar file na mile to naam text me dikhta hai */
function PartnerLogo({ name, file }: { name: string; file: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <span className="it-partner-text">{name}</span>;
  return <img src={img(file)} alt={name} loading="lazy" onError={() => setFailed(true)} />;
}

/* ============================== DATA ============================== */

const partners = [
  { name: "SAP", file: "sap (1).jpg" },
  { name: "Salesforce", file: "salesforce.jpg" },
  { name: "Microsoft", file: "microsoft.jpg" },
  { name: "AWS", file: "aws.svg" },
  { name: "Google Cloud", file: "google-cloud.svg" },
];

const pillars: { icon: IconKey; title: string; text: string }[] = [
  { icon: "people", title: "People", text: "Empower users and agents" },
  { icon: "gear", title: "Process", text: "Standardise and simplify" },
  { icon: "db", title: "Technology", text: "Integrate and automate" },
  { icon: "shield", title: "Governance", text: "Ensure compliance and control" },
  { icon: "trend", title: "Continuous Improvement", text: "Drive more value" },
];

const outcomes: { icon: IconKey; big: string; text: string }[] = [
  { icon: "clock", big: "50%", text: "Faster issue resolution" },
  { icon: "people", big: "35%", text: "Higher user satisfaction" },
  { icon: "gear", big: "30%", text: "Reduction in operational costs" },
];

const metrics = [
  { label: "Open Incidents", value: "12", delta: "↓ 40%", span: 2 },
  { label: "Service Requests", value: "28", delta: "↓ 30%", span: 2 },
  { label: "Changes", value: "6", delta: "↓ 50%", span: 2 },
  { label: "User Satisfaction", value: "4.7/5", delta: "↑ 20%", span: 3 },
  { label: "Automation Rate", value: "65%", delta: "↑ 35%", span: 3 },
];

const approach = [
  { title: "Assess", text: "Understand your current ITSM landscape and goals." },
  { title: "Design", text: "Define the right processes, tools and integrations." },
  { title: "Implement", text: "Deploy and configure with minimal disruption." },
  { title: "Optimise", text: "Measure, automate and continuously improve." },
];

const storyPoints: { icon: IconKey; text: string }[] = [
  { icon: "bolt", text: "Incident resolved in 15 minutes" },
  { icon: "ticket", text: "Automated service requests" },
  { icon: "smile", text: "Higher user satisfaction" },
];

const industries: { icon: IconKey; title: string }[] = [
  { icon: "factory", title: "Manufacturing" },
  { icon: "cart", title: "Retail & Consumer" },
  { icon: "bank", title: "Banking & Financial Services" },
  { icon: "bolt", title: "Energy & Utilities" },
  { icon: "heart", title: "Life Sciences & Healthcare" },
];

/* ============================== PAGE ============================== */

export default function ItsmPage() {
  const ref = useScrollReveal();

  return (
    <div className="it-page" ref={ref}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="it-hero" id="top">
          <div className="it-hero-bg" style={{ backgroundImage: `url("${imgHero}")` }} aria-hidden="true"></div>
          <div className="it-hero-overlay" aria-hidden="true"></div>

          <div className="it-hero-inner">
            <div className="it-hero-copy">
              <h1 data-reveal style={delay(0)}>
                <span className="it-line">Simpler IT.</span>
                <span className="it-line"><em>Smoother business.</em></span>
              </h1>
              <p data-reveal style={delay(1, 120)}>
                Our ITSM solutions help you deliver seamless IT services, resolve issues faster and create better
                experiences for your users.
              </p>
              <div className="it-actions" data-reveal style={delay(2, 120)}>
                <Link href="/tellusyourproblem#tell-details" className="it-btn it-btn-primary">
                  Talk to Our ITSM Experts <span aria-hidden="true">→</span>
                </Link>
                <Link href="#use-cases" className="it-btn it-btn-outline">
                  Explore Use Cases <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ============================= PARTNERS ============================ */}
        <section className="it-partners" aria-label="Technology partners">
          <div className="it-container">
            <div className="it-partners-row" data-reveal>
              <p>Trusted to modernise IT services across industries.</p>
              <ul>
                {partners.map((p) => (
                  <li key={p.name}><PartnerLogo name={p.name} file={p.file} /></li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ===================== BUILT AROUND YOUR BUSINESS ================== */}
        <section className="it-section" id="capabilities">
          <div className="it-container">
            <div className="it-intro" data-reveal>
              <h2>IT services built around your business.</h2>
              <p>
                We modernise IT service management with intelligent automation, seamless processes and a
                user-centric approach so your teams can focus on what matters.
              </p>
              {/* <Link href="/solutions/itsm/capabilities" className="it-link it-link--lg">
                Explore our capabilities <span aria-hidden="true">→</span>
              </Link> */}
            </div>

            <ul className="it-cards it-cards--five">
              {pillars.map((p, i) => (
                <li className="it-card" key={p.title} data-reveal style={delay(i, 80)}>
                  <span className="it-card-icon"><Icon name={p.icon} /></span>
                  <h3>{p.title}</h3>
                  <p>{p.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ============================= OUTCOMES ============================ */}
        <section className="it-section" aria-label="Business outcomes">
          <div className="it-container">
            <div className="it-head" data-reveal>
              <h2>Business outcomes</h2>
            </div>
            <ul className="it-cards it-cards--three">
              {outcomes.map((o, i) => (
                <li className="it-card it-card--stat" key={o.text} data-reveal style={delay(i, 80)}>
                  <strong>{o.big}</strong>
                  <p>{o.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ============================ AI TRANSFORM ========================= */}
        <section className="it-section">
          <div className="it-container">
            <div className="it-split it-split--rev">
              <div className="it-split-head" data-reveal>
                <h2>Transform IT service management with AI.</h2>
                <p>
                  Automate routine tasks, get proactive insights and deliver a better experience across your
                  organisation.
                </p>
              </div>

              <ul className="it-table" data-reveal style={delay(1, 100)}>
                {metrics.map((m) => (
                  <li key={m.label}>
                    <span className="it-table-label">{m.label}</span>
                    <strong>{m.value}</strong>
                    <em>{m.delta}</em>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ============================ APPROACH ============================= */}
        <section className="it-section">
          <div className="it-container">
            <div className="it-head" data-reveal>
              <h2>Our approach</h2>
              {/* <Link href="/how-we-work" className="it-link">See how it works <span aria-hidden="true">→</span></Link> */}
            </div>

            <ol className="it-cards it-cards--four">
              {approach.map((a, i) => (
                <li className="it-card" key={a.title} data-reveal style={delay(i, 90)}>
                  <span className="it-card-no">{String(i + 1).padStart(2, "0")}</span>
                  <h3>{a.title}</h3>
                  <p>{a.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ============================ CUSTOMER STORY ======================= */}
        <section className="it-section" id="use-cases">
          <div className="it-container">
            <div className="it-story">
              <div className="it-story-copy" data-reveal>
                <h2>A global retailer improves IT service efficiency by 40%.</h2>
                <p>
                  Orane helped a leading global retailer modernise their ITSM platform, automate service requests
                  and improve user satisfaction across multiple regions.
                </p>
                {/* <Link href="/customers/itsm" className="it-link">
                  Read the full story <span aria-hidden="true">→</span>
                </Link> */}
              </div>

              <div className="it-story-image" data-reveal style={delay(1, 100)}>
                <img src={imgStory} alt="" aria-hidden="true" loading="lazy" />
              </div>
            </div>

            <ul className="it-cards it-cards--three it-cards--after">
              {storyPoints.map((s, i) => (
                <li className="it-card it-card--row" key={s.text} data-reveal style={delay(i, 80)}>
                  <span className="it-card-icon"><Icon name={s.icon} /></span>
                  <h3>{s.text}</h3>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ============================= INDUSTRIES ========================== */}
        <section className="it-section">
          <div className="it-container">
            <div className="it-head it-head--row" data-reveal>
              <h2>Industries we serve</h2>
              {/* <Link href="/industries" className="it-link">Explore all industries <span aria-hidden="true">→</span></Link> */}
            </div>

            <ul className="it-cards it-cards--five">
              {industries.map((x, i) => (
                <li className="it-card it-card--row" key={x.title} data-reveal style={delay(i, 70)}>
                  <span className="it-card-icon"><Icon name={x.icon} /></span>
                  <h3>{x.title}</h3>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ============================ FINAL CTA ============================ */}
        <section className="it-cta" id="contact">
          <img className="it-cta-bg" src={imgCta} alt="" aria-hidden="true" />
          <div className="it-cta-copy">
            <span className="it-cta-eyebrow" data-reveal>READY TO MODERNISE YOUR IT SERVICES?</span>
            <h2 data-reveal style={delay(1, 120)}>Let&apos;s build a smarter ITSM function.</h2>
            <div className="it-actions" data-reveal style={delay(2, 120)}>
              <Link href="/tellusyourproblem#tell-details" className="it-btn it-btn-primary">
                Talk to Our Experts <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}